import React from 'react';
import classes from './Transactions.module.scss';
import { Button, Divider, Flex, Modal, Typography } from 'antd';
import TransactionIcon from '../components/TransactionIcon';
import { useIntl } from 'react-intl';
import { CloseOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import {
  Transaction,
  TransactionDetails,
  TransactionStatus,
  TransactionType
} from 'widgets/transactions/model/types';
import { ActivityIndicator, CenteredContainer, RoundContainer } from 'shared/ui';
import _ from 'lodash';
import { Icon } from '@iconify-icon/react';
import CardIcon from 'shared/ui/CardIcon/CardIcon';
import { useSelector } from 'react-redux';
import { RootState } from 'shared/config/store/store';
import { useMessage } from 'shared/libs/hooks/useMessage/useMessage';
import { downloadTransactionPdf, useLazyGetTransactionPdfQuery } from '../api/transactions';
import { Account } from 'entities/account/model/types';
import { getEMICurrentAccountObject } from 'shared/config/store/reducers/accountsSlice';
import { getCurrentEmi } from 'shared/config/store/selectors/app';
import { getUserMeta } from 'shared/config/store/reducers/userSlice';

const { Text, Title } = Typography;

type TransactionDetailsProps = {
  loading: boolean;
  transaction?: Transaction;
  transactionDetails?: TransactionDetails;
  onClose: () => unknown;
};

const TransactionDetailsModal: React.FC<TransactionDetailsProps> = ({
  loading,
  transaction,
  transactionDetails,
  onClose
}) => {
  const { formatMessage: t } = useIntl();
  const { showError, showSuccess } = useMessage();
  const [getTransactionPdf] = useLazyGetTransactionPdfQuery();
  const currentEmi = useSelector(getCurrentEmi);
  const currentAccount: Account = useSelector((state: RootState) =>
    getEMICurrentAccountObject(state, currentEmi),
  ) as Account;
  const token = useSelector((state: RootState) => state.user.token);
  const userMeta = useSelector(getUserMeta);
  const language = useSelector((state: RootState) => state.app.language);

  const handleExportPdf = async () => {
    if (!transaction?.id) return;
    try {
      await downloadTransactionPdf(getTransactionPdf, {
        accountId: currentAccount.id,
        transactionId: transaction.id,
        language: userMeta?.language || language || 'EN',
      });

      showSuccess(t({ id: 'transactionDetails.success' }));
    } catch (error) {
      console.error('Failed to download PDF:', error);
      showError(t({ id: 'transactionDetails.error' }));
    }
  };

  return (
    <Modal
      centered
      closeIcon={<CloseOutlined id='btn__transaction-close' />}
      open={transaction !== undefined}
      onCancel={onClose}
      className={classes['transaction-modal']}
      styles={{
        content: {
          minHeight: 400
        },
        footer: {
          display: 'flex',
          justifyContent: 'center',
          paddingBottom: 20
        }
      }}
      footer={null}
    >
      {(loading || !transaction || !transactionDetails) && (
        <div
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 100
          }}
        >
          <CenteredContainer>
            <ActivityIndicator size="large" />
          </CenteredContainer>
        </div>
      )}

      {transaction && transactionDetails && (
        <Flex vertical gap={16} justify='center' className={classes['transaction-details']}>
          <Text className={classes['transaction-details__header']}>
            {transactionDetails?.created || transactionDetails?.origin_created}
          </Text>
          <Flex vertical className={classes['transaction-details__body']}>
            <Flex vertical align="center" gap={8}>
              <div className={classes['icon-wrap']}>
                <TransactionIcon size={70} type={transaction.type} status={transaction.status} />
              </div>
              <Flex vertical gap={4}>
                <Text style={{ fontSize: 12 }}>
                  {_.startCase(
                    transactionDetails?.description || transactionDetails?.details?.description
                  )}
                </Text>
                <Title level={2} style={{ textAlign: 'center', fontWeight: 500 }}>
                  {transaction?.amount?.toFixed(2)}€
                </Title>
              </Flex>
            </Flex>
            <Divider style={{ margin: '20px 0 20px' }} />
            <Title
              level={5}
              style={{
                marginBottom: 12,
                fontWeight: 600
              }}
            >
              {t({ id: 'transactionDetails.details' })}
            </Title>
            <Flex gap={12} vertical>
              {transaction.type === TransactionType.AccountAdjustment &&
                transaction.recipientName && (
                  <Flex>
                    <Text style={{ fontSize: 12 }}>{t({ id: 'transactionDetails.payer' })}</Text>
                    <Text>{transaction.recipientName}</Text>
                  </Flex>
                )}
              {transaction.type === TransactionType.Authorization && (
                <Flex gap={10} vertical>
                  {transaction.recipientName && (
                    <Flex vertical>
                      <Text style={{ fontSize: 12, opacity: 0.5 }}>{t({ id: 'transactionDetails.merchant' })}</Text>
                      <Text>{transaction.recipientName}</Text>
                    </Flex>
                  )}
                  {transaction.terminalId && (
                    <Flex vertical>
                      <Text style={{ fontSize: 12, opacity: 0.5 }}>M{t({ id: 'transactionDetails.merchantId' })}</Text>
                      <Text>{transaction.terminalId}</Text>
                    </Flex>
                  )}
                  {transaction.merchantCountryCode && transaction.merchantCity && (
                    <Flex vertical>
                      <Text style={{ fontSize: 12, opacity: 0.5 }}>{t({ id: 'transactionDetails.merchantAddress' })}</Text>
                      <Text>
                        {transaction.merchantCountryCode}, {transaction.merchantCity}
                      </Text>
                    </Flex>
                  )}
                  {transaction.mcc && (
                    <Flex vertical>
                      <Text style={{ fontSize: 12, opacity: 0.5 }}>MCC</Text>
                      <Text>{transaction.mcc}</Text>
                    </Flex>
                  )}
                </Flex>
              )}
              {transaction.status && (
                <Flex vertical>
                  <Text style={{ fontSize: 12, opacity: 0.5 }}>{t({ id: 'transactionDetails.status' })}</Text>
                  <Text>{transaction.status}</Text>
                </Flex>
              )}
              {transactionDetails?.account && (
                <Flex vertical>
                  <Text style={{ fontSize: 12, opacity: 0.5 }}>{t({ id: 'transactionDetails.account' })}</Text>
                  <Text>
                    {transactionDetails.account.title}{' '}
                    <Text>({transactionDetails.account.iban})</Text>
                  </Text>
                </Flex>
              )}
              {transactionDetails?.card && (
                <Flex justify="space-between" align="center">
                  <Flex vertical>
                    <Text style={{ fontSize: 12, opacity: 0.5 }}>{t({ id: 'transactionDetails.card' })}</Text>
                    <Text>
                      {transactionDetails?.card.type === 'virtual'
                        ? t({ id: 'card.virtual' })
                        : t({ id: 'card.plastic' })}{' '}
                      *{transactionDetails?.card.number_masked.slice(-4)}
                    </Text>
                  </Flex>
                  <CardIcon size="s" cardNumber={transactionDetails?.card.number_masked} />
                </Flex>
              )}
              {transaction.type && (
                <Flex vertical>
                  <Text style={{ fontSize: 12, opacity: 0.5 }}>{t({ id: 'transactionDetails.type' })}</Text>
                  <Text>{_.startCase(transaction.type)}</Text>
                </Flex>
              )}
            </Flex>
            {transaction?.status !== TransactionStatus.Declined && (
              <Button
                disabled={loading}
                type="primary"
                size="small"
                style={{ marginTop: 32, backgroundColor: 'black' }}
                id="btn__download-transaction"
                onClick={handleExportPdf}
              >
                {t({ id: 'screens.history.single.buttons.pdf' })}
              </Button>
            )}
          </Flex>
        </Flex>
      )}
    </Modal>
  );
};

export default TransactionDetailsModal;
